/**
 * Feature Flag Hook
 * 
 * Provides React hook interface for feature flags
 * 
 * Usage:
 *   const isEnabled = useFeatureFlag('UNIFIED_WORK_QUEUE');
 *   const allFlags = useFeatureFlags();
 */

import { useState, useEffect } from 'react';
import { 
  FEATURE_FLAGS, 
  FeatureFlagKey, 
  isFeatureEnabled,
  getAllFeatureFlags,
  FEATURE_FLAG_DESCRIPTIONS 
} from '../config/featureFlags';

/**
 * Hook to check if a single feature flag is enabled
 * 
 * @param flag - The feature flag to check
 * @returns boolean - Whether the feature is enabled
 * 
 * @example
 * function MyComponent() {
 *   const useNewQueue = useFeatureFlag('UNIFIED_WORK_QUEUE');
 *   
 *   if (useNewQueue) {
 *     return <UnifiedWorkQueue />;
 *   }
 *   return <LegacyQueue />;
 * }
 */
export function useFeatureFlag(flag: FeatureFlagKey): boolean {
  // For now, just return the static value
  // This hook structure allows future enhancements like:
  // - Real-time updates from server
  // - User-specific overrides
  // - A/B testing integration
  return isFeatureEnabled(flag);
}

/**
 * Hook to get all feature flags
 * Useful for debugging or admin panels
 * 
 * @returns All feature flags with their current values
 */
export function useFeatureFlags(): typeof FEATURE_FLAGS {
  return getAllFeatureFlags();
}

/**
 * Hook to get feature flag with its description
 * Useful for building feature flag admin UI
 */
export function useFeatureFlagWithDescription(flag: FeatureFlagKey): {
  enabled: boolean;
  description: string;
  key: string;
} {
  return {
    key: flag,
    enabled: isFeatureEnabled(flag),
    description: FEATURE_FLAG_DESCRIPTIONS[flag],
  };
}

/**
 * Hook to get all flags with descriptions
 * Useful for building a feature flags dashboard
 */
export function useAllFeatureFlagsWithDescriptions(): Array<{
  key: FeatureFlagKey;
  enabled: boolean;
  description: string;
}> {
  const flags = getAllFeatureFlags();
  return (Object.keys(flags) as FeatureFlagKey[]).map(key => ({
    key,
    enabled: flags[key],
    description: FEATURE_FLAG_DESCRIPTIONS[key],
  }));
}

// Re-export types for convenience
export type { FeatureFlagKey };
